import { LocaleStartupError } from '@/components/erp/LocaleStartupError'
import React from 'react'
import { createRoot } from 'react-dom/client'
import 'sweetalert2/dist/sweetalert2.min.css'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { store, persistor } from '@/redux/store'
import ErrorBoundary from '@/components/ErrorBoundary'
import App from './App'
import { setLocale } from '@/i18n'
import { preferredLocale } from '@/config/locales' // Initialize i18n
import '@/index.css'
import { initEcho } from '@/services/echoClient'
import { installRealtimeResync } from '@/services/realtimeResync'

// Realtime: create the Echo client before React mounts so every hook that
// reads window.Echo on mount finds a live connection (page-refresh case;
// fresh logins re-init from the auth slice).
initEcho()
// Events broadcast while the socket was down are lost — after a reconnect,
// invalidate the realtime-fed cache tags so mounted views refetch.
installRealtimeResync()

// Boot race: initEcho() above runs before PersistGate rehydrates the token,
// so it exits with `no-token` and nothing retries it unless a session action
// fires. Re-attempt once rehydration completes so a hard refresh with a
// persisted session always gets a live socket.
// The subscriber fires on EVERY persist cycle, not once, so it removes itself
// as soon as rehydration has been observed — otherwise initEcho() is re-invoked
// on each later state write.
const unsubscribeBoot = persistor.subscribe(() => {
  const state = store.getState() as any;
  const bootstrapped = (state as any)?._persist?.rehydrated;
  if (bootstrapped) {
    unsubscribeBoot()
    try {
      initEcho();
    } catch {
      // initEcho is idempotent and self-guards; boot must never fail on it.
    }
  }
})

const container = document.getElementById('app')

if (container) {
  const root = createRoot(container)
  const renderApplication = () => {
    root.render(
      <React.StrictMode>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <ErrorBoundary>
              <App />
            </ErrorBoundary>
          </PersistGate>
        </Provider>
      </React.StrictMode>
    )

    const loadingWrap = document.getElementById('loading_wrap')
    if (loadingWrap) {
      loadingWrap.style.display = 'none'
    }
  }
  const start = async () => {
    const locale = preferredLocale()
    if (await setLocale(locale)) renderApplication()
    else {
      const loading = document.getElementById('loading_wrap')
      if (loading) loading.style.display = 'none'
      root.render(<LocaleStartupError locale={locale} retry={() => { void start() }} />)
    }
  }
  void start()
}
